/**
 * PaywallProductsPanel.tsx — 支付产品「逻辑产品分组视图」（重设计，移植自 design/Novel-admin paywall-products.jsx；
 * 框架通道行按 PayerMax 设计稿 B1–B4）
 *
 * 一个逻辑产品 = 一个 access_level_id，其下按支付通道各一行（#387 §2 hybrid）。**UI 一律呈现为「支付商品」**
 * （走查 #619 ④）；「逻辑产品」只留在代码与注释里，对应 `LogicalProduct` 类型与 dev-docs 术语，两者同指一物。
 * **通道模型 = 结算通道闭集派生**：payment_method 取 `SETTLEMENT_CHANNELS`（IAP / STRIPE / 已登记框架通道），
 * IAP 内购商店名 + 图标由 app.platform 派生（iOS→App Store / Android→Google Play）；齐备 = 一条 IAP 行 + 至少一条
 * 非 IAP 行（shared `channelRowGapsOf`）。框架通道行另有原生成交价（`channel_prices_summary`，A8 §1），点价格标签
 * 进 `ChannelPriceEditorDialog`（A8 §10.3）。数据走 useProductMetadata + groupProductMetadataByAccessLevel；
 * 业务效果 CRUD 走 productMetadataApi（编辑复用 ProductMetadataEditor）；启停/删除高危收窄 root/admin。
 * Adapty 关联面板（AdaptyLinks）见 PaywallAdaptyLinks（消费 adapty_product_links）。
 */

import {
  type ProductValidationIssue,
  type SettlementChannel,
  isFrameworkSettlementChannel,
  isIapPairedSettlementChannel,
} from '@novel-admin/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import {
  channelPriceSummaryState,
  formatChannelPriceMoney,
  formatChannelPriceTag,
} from '~/data/adapters/channel-price-adapter';
import {
  consumableEffectLabel,
  formatDurationSeconds,
  groupDigits,
  readingPassDurationSecondsOf,
  readingPassTierIdOf,
  subscriptionEffectLabel,
} from '~/data/adapters/product-metadata-adapter';
import {
  type AddableChannel,
  CHANNEL_DISPLAY_ORDER,
  type ChannelCategory,
  type DivergentKey,
  type LogicalProduct,
  type ReadingPassCoverage,
  addableChannelsOf,
  channelCategory,
  channelRowGapLabel,
  channelShortLabel,
  channelStoreLabel,
  computeReadingPassCoverage,
  groupProductMetadataByAccessLevel,
  pairedChannelNames,
} from '~/data/adapters/product-metadata-grouping';
import {
  type ProductMetadataRow,
  useAdaptyCatalog,
  usePaymentPages,
  useProductMetadata,
} from '~/data/queries';
import { adaptyProductLinksApi } from '~/hooks/use-adapty-product-links';
import { productMetadataApi } from '~/hooks/use-product-metadata';
import { Icons } from '~/lib/icons';
import { useAuthStore } from '~/stores/auth-store';
import { ContextMenu } from '~/ui/ContextMenu';
import { ConfirmDialog } from '~/ui/Dialog';
import { ExpandToggleButton } from '~/ui/ExpandToggleButton';
import { FilterButton } from '~/ui/FilterButton';
import { SearchInput } from '~/ui/SearchInput';
import { toast } from '~/ui/Toast';
import { PaywallAdaptyLinks } from './PaywallAdaptyLinks';
import { ChannelPriceEditorDialog } from './dialogs/ChannelPriceEditorDialog';
import {
  ProductMetadataEditor,
  isCanonicalStripeVendorProductId,
} from './dialogs/ProductMetadataEditor';

/** 当前展示 App（platform 用于派生内购商店名）*/
export interface PanelApp {
  id: string;
  name: string;
  platform?: string;
}

/**
 * 通道图标：STRIPE→钱包；框架通道（PayerMax）→地球（设计稿 B1 的通道标识）；IAP→iOS/Android 分别映射商店图标，
 * 未配置/未知→通用内购图标。
 */
function channelIcon(method: SettlementChannel, platform: string | undefined) {
  if (method === 'STRIPE') return Icons.IconWallet;
  if (isFrameworkSettlementChannel(method)) return Icons.IconGlobe;
  if (platform?.includes('iOS')) return Icons.IconApple;
  if (platform?.includes('Android')) return Icons.IconGooglePlay;
  return Icons.IconCoin;
}

/** 通道标识 chip 的配色：STRIPE 沿用 info 色，框架通道与 IAP 用中性 `.chip`（设计稿 B1）*/
function channelChipClass(method: SettlementChannel): string {
  return method === 'STRIPE' ? 'chip chip-info' : 'chip';
}

const CATEGORY_LABEL: Record<ChannelCategory, { label: string; hint: string }> = {
  IAP: { label: '内购 IAP', hint: '应用内支付（商店按 App 平台派生）' },
  WEB: { label: '三方支付', hint: 'Stripe / PayerMax · 各自独立配置' },
};
const CATEGORY_ORDER: ChannelCategory[] = ['IAP', 'WEB'];

const isSub = (g: LogicalProduct) => g.productType === 'SUBSCRIPTION';
const typeLabel = (g: LogicalProduct) => (isSub(g) ? '订阅' : '充值');
const expireLabel = (d: number) => (d === 0 ? '永久' : `${d} 天`);

/** 不可售原因（A8 §1 validation_issues 闭集）→ 中文提示（运营发布 gate 排障用）*/
const VALIDATION_ISSUE_LABEL: Record<ProductValidationIssue, string> = {
  INACTIVE: '已停用',
  // A8 §1：关联固定、不经 PATCH 补写 —— 补救 = 建关联 → root/admin 删行 → 带 access_level_id 重录（走复活）
  ACCESS_LEVEL_ID_MISSING:
    '缺 access_level（先建立该产品的 access level 关联，再由 root/admin 删除该行并带该 access_level_id 重新录入，走复活）',
  COIN_AMOUNT_NON_POSITIVE: 'coin 非正',
  PRODUCT_FIELDS_INVALID: '字段不自洽',
  IAP_BASELINE_MISSING: '缺 IAP 基线',
  COIN_MISMATCH: 'coin ≠ IAP 基线',
  // 承接 product_type / 订阅效果与周期 / 一次性效果与参数的跨通道不一致（A8 §1）
  PRODUCT_FIELDS_MISMATCH: '与 IAP 基线字段不符',
  BELOW_MINIMUM_BONUS: 'bonus 低于派生下限',
  UNSUPPORTED_STRIPE_SUBSCRIPTION_PERIOD: 'STRIPE 订阅仅 WEEKLY / MONTHLY',
  READING_PASS_TIER_INCOMPLETE: '该通道另一产品位未配齐',
  CHANNEL_PRICE_UNSELECTABLE: '该通道未配置可用价格（未配价、多个币种行或币种不可定价）',
  EFFECT_FULFILLMENT_NOT_IMPLEMENTED: '平台尚未履约，不必改本行',
};
/** 行的不可售原因串（tooltip 用）；可售返 '' */
function unserveableReason(c: ProductMetadataRow): string {
  if (c.isServeable) return '';
  return c.validationIssues.map((i) => VALIDATION_ISSUE_LABEL[i] ?? i).join(' · ');
}

/**
 * per-通道权益简写（聚合摘要用）：发币档显 coin+bonus(+ratio%) —— 充值每笔一次、`COIN_PERIOD_GRANT` 订阅
 * 每计费周期一次（同一套 coin / bonus，70 §2）；不发币档（阅读卡 / 买断 / VIP 畅读）显 +bonus（恒 0）。
 */
function benefitBrief(c: ProductMetadataRow): string {
  const coin = Number(c.coinAmount);
  const perPeriod = c.productType === 'SUBSCRIPTION' ? '/期' : '';
  if (coin > 0) {
    const ratio = Number(c.bonusRatio);
    return `${groupDigits(c.coinAmount)}+${groupDigits(c.bonusAmount)}${perPeriod}${ratio ? ` +${Math.round(ratio * 100)}%` : ''}`;
  }
  return `+${groupDigits(c.bonusAmount)}${perPeriod}`;
}

/**
 * 逻辑产品的效果摘要串（分组头徽章）：发币直接显效果名；阅读卡额外带产品位与当前时长
 * —— 运营排查「为什么这个商品不出现在支付页」时，效果与产品位是首屏就该看见的字段（issue #400 第 3 条）。
 *
 * **只对 CONSUMABLE 调用**（订阅由调用方跳过，见分组头渲染）：`consumable_effect` 契约上只有
 * CONSUMABLE 能非空（A8 §2，`assertConsumableEffectProductType` 对 SUBSCRIPTION 传非 null 直接拒），
 * 故订阅行的它恒 `null`；原先给订阅渲染的「订阅（无效果）」徽章对每个订阅商品恒亮、零信息量，
 * 且「无效果」读起来像「这订阅没配好」（#619 ⑤）。订阅的效果走 `subscription_effect`，由分组头单独的
 * 订阅效果徽章表达（subscriptionEffectLabel）。
 */
function effectBrief(g: LogicalProduct): string {
  const { effect, tierId, durationSeconds } = g.effect;
  if (effect === null) return '未登记效果';
  if (effect !== 'READING_PASS_CARD') return consumableEffectLabel(effect);
  return `${consumableEffectLabel(effect)} · ${tierId ?? '产品位缺失'} · ${formatDurationSeconds(durationSeconds)}`;
}

/**
 * 效果徽章配色（走查 #619 ②）：**按「卖的是什么」分两类**，不是按 `product_type` 分。
 * - `chip-info`（发币类）：发币 `COIN_GRANT` —— 买了给钱包加值，与订阅同属「加额度」这一类；
 * - `chip-accent`（权益类）：小时阅读卡 + 书籍买断 —— 买的是一张卡 / 一本书，履约链路与排障口径都另一套；
 * - `chip-neg`：未登记效果 —— 是配置事故，不属于任何一类，保持错误色。
 *
 * 订阅不进本函数（分组头不给订阅渲染效果徽章，见 `effectBrief` 文件注释）。
 */
function effectChipClass(g: LogicalProduct): string {
  const { effect } = g.effect;
  if (effect === null) return 'chip-neg';
  return effect === 'COIN_GRANT' ? 'chip-info' : 'chip-accent';
}

/** 通道行的效果摘要（仅阅读卡行渲染；发币行的权益已在 COIN/BONUS 列表达，不重复占位）*/
function channelEffectBrief(c: ProductMetadataRow): string | null {
  if (c.consumableEffect !== 'READING_PASS_CARD') return null;
  const tier = readingPassTierIdOf(c);
  return `${tier ?? '产品位缺失'} · ${formatDurationSeconds(readingPassDurationSecondsOf(c))}`;
}

/** 复制到剪贴板 + toast 轻反馈（access_level / vendor_product_id 复制按钮 + 右键菜单共用）*/
function copyToClipboard(text: string, label: string) {
  navigator.clipboard?.writeText(text);
  toast(`已复制 ${label}`, { tone: 'pos', icon: 'IconCopy' });
}

/**
 * 内联复制按钮（设计 pm-al / pm-vid 样式）：可选 prefix 弱化前缀 + mono 值 + IconCopy，hover 高亮。
 * pm-al = access_level（prefix「access_level」+ accent-fg 值）；pm-vid = vendor_product_id（纯值）。
 */
function CopyChipButton({
  prefix,
  value,
  valueColor,
  copyAs,
  maxWidth,
}: {
  prefix?: string;
  value: string;
  valueColor?: string;
  copyAs: string;
  maxWidth?: number | string;
}) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      className="focusable"
      title={`复制 ${value}`}
      onClick={() => copyToClipboard(value, copyAs)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        border: 0,
        background: hover ? 'var(--bg-muted)' : 'transparent',
        cursor: 'pointer',
        padding: '1px 5px',
        borderRadius: 5,
        font: 'inherit',
        fontSize: 10.5,
        color: 'var(--fg-muted)',
        minWidth: 0,
        flex: '0 1 auto',
        maxWidth: maxWidth ?? '100%',
      }}
    >
      {prefix && <span style={{ color: 'var(--fg-faint)', fontWeight: 400 }}>{prefix}</span>}
      <span
        className="mono"
        style={{
          fontSize: 10.5,
          color: valueColor ?? 'var(--fg)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          minWidth: 0,
        }}
      >
        {value}
      </span>
      <Icons.IconCopy size={9} style={{ color: 'var(--fg-faint)', flexShrink: 0 }} />
    </button>
  );
}

/** 写动作角色集（root/admin/operator）；启停/删除高危收窄 root/admin */
function useCanWrite() {
  return useAuthStore(
    (s) => s.user?.role === 'root' || s.user?.role === 'admin' || s.user?.role === 'operator',
  );
}
function useCanManage() {
  return useAuthStore((s) => s.user?.role === 'root' || s.user?.role === 'admin');
}

export function PaywallProductsPanel({ app }: { app: PanelApp }) {
  const { data: rows = [], isLoading, isSuccess } = useProductMetadata(app.id);
  const queryClient = useQueryClient();
  const canWrite = useCanWrite();
  const canManage = useCanManage();
  const noApp = app.id === 'all';

  const [q, setQ] = useState('');
  const [kinds, setKinds] = useState<string[]>([]);
  const [channels, setChannels] = useState<string[]>([]);
  const [statuses, setStatuses] = useState<string[]>([]);
  const [levels, setLevels] = useState<string[]>([]);
  const [pageIds, setPageIds] = useState<string[]>([]);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  // 逻辑产品筛选 App-specific → 切 App 重置（防旧 access_level 过滤新 App 空列表）
  // biome-ignore lint/correctness/useExhaustiveDependencies: 仅 app.id 变化时重置
  useEffect(() => {
    setLevels([]);
    setPageIds([]);
  }, [app.id]);

  // 编辑/新建弹窗（复用 ProductMetadataEditor）；删除 / 停用二次确认
  const [editor, setEditor] = useState<{
    item?: ProductMetadataRow;
    prefillTitle?: string;
    prefillAccessLevel?: string;
    /** 「补通道」时同逻辑产品的既有行 —— 预填效果与效果参数（§2.5 跨通道一致性）*/
    prefillEffectFrom?: ProductMetadataRow;
    /** 该逻辑产品的全部通道行 —— 改 duration_seconds 的生命周期 gate 判据（A8 §3）*/
    pairedChannels?: ProductMetadataRow[];
    /** 「补通道 · X」预选的通道 */
    prefillPaymentMethod?: SettlementChannel;
    /** 「补通道」时该逻辑产品已有行的通道（通道下拉里置灰，每 product 每通道一行）*/
    existingChannels?: SettlementChannel[];
    /** 「补通道」时组内既有配对行的派生 bonus 下限（展示「BONUS（≥ N）」）*/
    prefillBonusFloor?: string;
  } | null>(null);
  // 框架通道行的原生价格编辑（设计稿 B3）
  const [pricing, setPricing] = useState<{ row: ProductMetadataRow; productName: string } | null>(
    null,
  );
  const [deleting, setDeleting] = useState<ProductMetadataRow | null>(null);
  const [deactivating, setDeactivating] = useState<ProductMetadataRow | null>(null);
  // 整组下架二次确认（整组上架无破坏性，直接批量）
  const [deactivatingGroup, setDeactivatingGroup] = useState<LogicalProduct | null>(null);
  // 换代并下架其他卡（A8 §5.1 高危 DLQ 路径，默认不走）
  const [replacingOthers, setReplacingOthers] = useState<LogicalProduct | null>(null);
  // 换代上架确认（设计稿 B4：列出参与行，框架通道参与行未定价时提示仍不可售）
  const [replacing, setReplacing] = useState<LogicalProduct | null>(null);

  const { groups, unassigned } = useMemo(() => groupProductMetadataByAccessLevel(rows), [rows]);

  /*
    「支付页面」筛选（#619 ④）—— 按 **Adapty 实际挂载关系**反查该页会展示哪些支付商品，链路四跳：
      payment_pages.placements[渠道] → catalog.placements[].products[]（Adapty 内部 product_id）
      → adapty_product_links → access_level_id
    注意这**不是** A8 §7.9 的「候选产品」口径 —— 那里每页候选 = 该 App 全部支付商品（供先排序后上架），
    按那个口径筛等于不筛。本筛选答的是运营真正要问的「这个页面现在实际会展示哪些商品」。
    三层配置缺任一环该页就解析不出商品（页没配 placement / paywall_name 没匹配上 / 运营没做产品关联），
    故选项里直接把可解析条数摆出来，0 条给 hint 说明去哪补，不静默让运营以为「该页就是没商品」。
  */
  const paymentPages = usePaymentPages(app.id);
  const adaptyCatalog = useAdaptyCatalog(app.id);
  const productLinks = useQuery({
    queryKey: ['adapty-product-links', app.id],
    queryFn: () => adaptyProductLinksApi.list(app.id),
    enabled: !noApp,
  });
  const pageAccessLevels = useMemo(() => {
    const linkMap = new Map(
      (productLinks.data ?? []).map((l) => [l.adapty_product_id, l.access_level_id]),
    );
    // placement_id → 该 placement 的 paywall 下、已关联到我方支付商品的 access_level 集合
    const byPlacement = new Map<string, Set<string>>();
    for (const pl of adaptyCatalog.data?.placements ?? []) {
      const set = new Set<string>();
      for (const pr of pl.products ?? []) {
        const al = linkMap.get(pr.product_id);
        if (al) set.add(al);
      }
      byPlacement.set(pl.placement_id, set);
    }
    // 页面 → 各渠道 placement 的**并集**：同页两渠道指向不同 placement 时两边都算数，
    // 「IAP 有 / Stripe 没有」是配置态、不是筛选该隐藏的理由（齐备性由组头的通道徽章表达）。
    const byPage = new Map<string, Set<string>>();
    for (const page of paymentPages.data) {
      const set = new Set<string>();
      for (const placementId of Object.values(page.placements)) {
        for (const al of byPlacement.get(placementId) ?? []) set.add(al);
      }
      byPage.set(page.id, set);
    }
    return byPage;
  }, [paymentPages.data, adaptyCatalog.data, productLinks.data]);
  // 小时阅读卡按 tier 报缺哪个通道（#773）
  const readingPassCoverage = useMemo(() => computeReadingPassCoverage(groups), [groups]);

  const stat = {
    products: groups.length,
    recharge: groups.filter((g) => !isSub(g)).length,
    sub: groups.filter((g) => isSub(g)).length,
    active: rows.filter((c) => c.isActive).length,
    disabled: rows.filter((c) => !c.isActive).length,
    // 在架但不可售（#465 读时派生，含框架通道未定价）：首购不进 C 端 serving
    unserveable: rows.filter((c) => c.isActive && !c.isServeable).length,
    channelRows: rows.length,
  };
  const halfConfig = groups.filter((g) => g.readiness.level === 'warn');

  const deleteMutation = useMutation({
    mutationFn: (id: string) => productMetadataApi.remove(id),
    onSuccess: async () => {
      toast('已删除通道行（软删）', { tone: 'warn', icon: 'IconTrash' });
      await queryClient.invalidateQueries({ queryKey: ['product-metadata', app.id] });
      setDeleting(null);
    },
    onError: (err) =>
      toast(err instanceof Error ? err.message : '删除失败', { tone: 'neg', icon: 'IconAlert' }),
  });
  const toggleMutation = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      active ? productMetadataApi.activate(id) : productMetadataApi.deactivate(id),
    onSuccess: async (_d, { active }) => {
      toast(active ? '已上架 · C 端恢复发放' : '已下架 · C 端停止发放', {
        tone: active ? 'pos' : 'warn',
        icon: active ? 'IconCheck' : 'IconBan',
      });
      setDeactivating(null);
      await queryClient.invalidateQueries({ queryKey: ['product-metadata', app.id] });
    },
    onError: (err) =>
      toast(err instanceof Error ? err.message : '操作失败', { tone: 'neg', icon: 'IconAlert' }),
  });
  // 整组启停：逐行调 activate/deactivate（仅打需变更的行），一次性 toast + invalidate
  const groupToggleMutation = useMutation({
    mutationFn: ({ rows: rs, active }: { rows: ProductMetadataRow[]; active: boolean }) =>
      Promise.all(
        rs.map((r) =>
          active ? productMetadataApi.activate(r.id) : productMetadataApi.deactivate(r.id),
        ),
      ),
    onSuccess: async (_d, { rows: rs, active }) => {
      toast(
        active ? `整组上架 · ${rs.length} 通道恢复发放` : `整组下架 · ${rs.length} 通道停止发放`,
        { tone: active ? 'pos' : 'warn', icon: active ? 'IconCheck' : 'IconBan' },
      );
      setDeactivatingGroup(null);
      await queryClient.invalidateQueries({ queryKey: ['product-metadata', app.id] });
    },
    onError: (err) =>
      toast(err instanceof Error ? err.message : '批量操作失败', {
        tone: 'neg',
        icon: 'IconAlert',
      }),
  });
  const otherActivePassIds = (accessLevelId: string) =>
    groups
      .filter(
        (other) =>
          other.effect.effect === 'READING_PASS_CARD' &&
          other.accessLevelId !== accessLevelId &&
          other.readiness.gaps.length === 0 &&
          other.channels.some((c) => c.isActive),
      )
      .map((other) => other.accessLevelId);

  const replaceMutation = useMutation({
    mutationFn: ({ accessLevelId, deactivate }: { accessLevelId: string; deactivate: string[] }) =>
      productMetadataApi.replaceReadingPassTiers({
        app_id: app.id,
        activate: [accessLevelId],
        deactivate,
      }),
    onSuccess: async (_d, { deactivate }) => {
      toast(deactivate.length > 0 ? '已换代并下架其他卡' : '已换代上架', {
        tone: 'pos',
        icon: 'IconCheck',
      });
      setReplacingOthers(null);
      setReplacing(null);
      await queryClient.invalidateQueries({ queryKey: ['product-metadata', app.id] });
    },
    onError: (err) =>
      toast(err instanceof Error ? err.message : '换代失败', { tone: 'neg', icon: 'IconAlert' }),
  });

  // 筛选
  const activeFilter = !!(
    q ||
    kinds.length ||
    channels.length ||
    statuses.length ||
    levels.length ||
    pageIds.length
  );
  const ql = q.trim().toLowerCase();
  // 硬筛选（通道 + 状态）：任何时候都逐行应用，即便组名/access_level 命中搜索也不放行不符行
  const matchHardFilters = (c: ProductMetadataRow) => {
    if (channels.length && !channels.includes(c.paymentMethod)) return false;
    if (statuses.length && !statuses.includes(c.isActive ? '1' : '0')) return false;
    return true;
  };
  // 行级文本匹配（vendor_product_id）：仅此项在组名/access_level 命中（gHit）时对行免除
  const matchQl = (c: ProductMetadataRow) => !ql || c.vendorProductId.toLowerCase().includes(ql);
  const filteredGroups = groups
    .map((g) => {
      if (kinds.length && !kinds.includes(g.productType)) return null;
      if (levels.length && !levels.includes(g.accessLevelId)) return null;
      if (pageIds.length && !pageIds.some((id) => pageAccessLevels.get(id)?.has(g.accessLevelId)))
        return null;
      const gHit =
        !!ql &&
        (g.name.toLowerCase().includes(ql) ||
          g.accessLevelId.toLowerCase().includes(ql) ||
          g.title.toLowerCase().includes(ql));
      // gHit 只免除行级 ql 文本，不免除通道/状态硬筛选；有硬筛选时被筛空的组仍剔除
      const chans = g.channels.filter((c) => matchHardFilters(c) && (gHit || matchQl(c)));
      if ((channels.length || statuses.length || ql) && chans.length === 0) return null;
      return { g, chans };
    })
    .filter((x): x is { g: LogicalProduct; chans: ProductMetadataRow[] } => x !== null);

  // 未归组行没有 access_level，挂不到任何 paywall 上 —— 按 access_level 或按页面筛时一律不显
  const showUnassigned = !levels.length && !pageIds.length;
  // 未归组行同样受 kinds（类型）+ 硬筛选 + ql 文本约束（补齐漏掉的 kinds 筛选）
  const filteredUnassigned = unassigned.filter(
    (u) => (!kinds.length || kinds.includes(u.productType)) && matchHardFilters(u) && matchQl(u),
  );

  const levelOptions = groups.map((g) => ({ value: g.accessLevelId, label: g.accessLevelId }));
  const channelCount = (m: SettlementChannel) => rows.filter((c) => c.paymentMethod === m).length;
  // 「通道」筛选项按结算通道闭集生成（含已登记框架通道），各带行数
  const channelFilterOptions = CHANNEL_DISPLAY_ORDER.map((m) => ({
    value: m,
    label:
      m === 'IAP'
        ? '内购 (IAP)'
        : m === 'STRIPE'
          ? channelStoreLabel(m, app.platform)
          : `三方 (${channelStoreLabel(m, app.platform)})`,
    count: channelCount(m),
  }));

  const toggleCollapse = (id: string) => setCollapsed((m) => ({ ...m, [id]: !m[id] }));
  const collapseAll = (v: boolean) =>
    setCollapsed(v ? Object.fromEntries(groups.map((g) => [g.accessLevelId, true])) : {});

  const exportCsv = () => {
    const head = [
      'access_level_id',
      'name',
      'product_type',
      // 订阅效果（70 §2）：COIN_PERIOD_GRANT 每计费周期一次性发放 coin_amount + bonus_amount
      'subscription_effect',
      'subscription_period',
      'payment_method',
      'vendor_product_id',
      'coin_amount',
      'bonus_amount',
      'bonus_ratio',
      'bonus_expire_days',
      // 效果三列（dd614）：排查不可售根因时要能离线核对效果 / 产品位 / 时长
      'consumable_effect',
      'tier_id',
      'duration_seconds',
      'is_active',
    ];
    // 逐行的效果三列（tier_id / duration_seconds 仅阅读卡行非空）
    const effectCells = (c: ProductMetadataRow): (string | number)[] => [
      c.consumableEffect ?? '',
      readingPassTierIdOf(c) ?? '',
      readingPassDurationSecondsOf(c) ?? '',
    ];
    const lines: (string | number | boolean)[][] = [];
    for (const g of groups)
      for (const c of g.channels)
        lines.push([
          g.accessLevelId,
          g.name,
          g.productType,
          c.subscriptionEffect ?? '',
          g.subscriptionPeriod ?? '',
          c.paymentMethod,
          c.vendorProductId,
          c.coinAmount,
          c.bonusAmount,
          c.bonusRatio,
          c.bonusExpireDays,
          ...effectCells(c),
          c.isActive,
        ]);
    for (const u of unassigned)
      lines.push([
        '',
        u.name,
        u.productType,
        u.subscriptionEffect ?? '',
        u.subscriptionPeriod ?? '',
        u.paymentMethod,
        u.vendorProductId,
        u.coinAmount,
        u.bonusAmount,
        u.bonusRatio,
        u.bonusExpireDays,
        ...effectCells(u),
        u.isActive,
      ]);
    const csv = [head, ...lines]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `product_metadata_${app.id}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast(`已导出 ${rows.length} 行 · CSV`, { tone: 'pos', icon: 'IconDownload' });
  };

  const empty = rows.length === 0;

  return (
    <div className="vstack" style={{ gap: 12 }}>
      {/* 信息 banner */}
      <div
        className="card"
        style={{ padding: 14, background: 'var(--info-soft)', borderColor: 'transparent' }}
      >
        <div className="hstack" style={{ gap: 10, alignItems: 'flex-start' }}>
          <Icons.IconShield
            size={14}
            style={{ color: 'var(--info)', marginTop: 2, flexShrink: 0 }}
          />
          <div style={{ flex: 1, fontSize: 12.5, lineHeight: 1.6 }}>
            <span style={{ fontWeight: 500 }}>{app.name} · 支付产品（业务效果）</span>
            <span style={{ color: 'var(--fg-muted)', marginLeft: 6 }}>
              <span className="mono">Adapty product_id</span> / 价格 / 标题 / 周期 / 上下架由 Adapty
              Dashboard 承接 · 本后台维护 coin / bonus 业务效果 + 手输{' '}
              <span className="mono">vendor_product_id</span> 与{' '}
              <span className="mono">access_level_id</span>；PayerMax 行另配原生成交价。
            </span>
            {/* 图例：三键 / 通道模型速览（齐备判据 = 一条 IAP 行 + 至少一条非 IAP 行，70 §2）*/}
            <div
              className="hstack"
              style={{
                gap: 14,
                marginTop: 8,
                flexWrap: 'wrap',
                fontSize: 11.5,
                color: 'var(--fg-muted)',
              }}
            >
              <span className="hstack" style={{ gap: 5 }}>
                <span className="chip chip-accent" style={{ fontSize: 9.5 }}>
                  分组键
                </span>
                <span className="mono" style={{ color: 'var(--fg)' }}>
                  access_level_id
                </span>
                跨渠道同值 · 客户端匹配 · 空=未归组
              </span>
              <span className="hstack" style={{ gap: 5 }}>
                <span className="chip" style={{ fontSize: 9.5 }}>
                  履约键
                </span>
                <span className="mono" style={{ color: 'var(--fg)' }}>
                  vendor_product_id
                </span>
                per 通道 · 服务端 webhook 反查
              </span>
              <span className="hstack" style={{ gap: 5 }}>
                <span className="chip" style={{ fontSize: 9.5 }}>
                  通道
                </span>
                内购 IAP（按 App 平台派生 App Store / Google Play）+ 三方支付（Stripe / PayerMax）·
                各自独立配置 · 齐备 = 一条 IAP 行 + 至少一条非 IAP 行
              </span>
            </div>
          </div>
          {!empty && canWrite && !noApp && (
            <button
              className="btn btn-sm btn-accent"
              style={{ flexShrink: 0 }}
              title="先建 product 级信息，再逐通道补 vendor id + 业务效果"
              onClick={() => setEditor({})}
            >
              <Icons.IconPlus size={12} />
              新建支付商品
            </button>
          )}
        </div>
      </div>

      {/* 半配 / 未归组告警 */}
      {(halfConfig.length > 0 || unassigned.length > 0) && (
        <div
          className="card"
          style={{ padding: 12, background: 'var(--warn-soft)', borderColor: 'transparent' }}
        >
          <div className="hstack" style={{ gap: 10, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <Icons.IconAlert
              size={14}
              style={{ color: 'var(--warn)', marginTop: 2, flexShrink: 0 }}
            />
            <div style={{ flex: 1, minWidth: 200, fontSize: 12.5, lineHeight: 1.55 }}>
              <span style={{ fontWeight: 500 }}>发布前需清零</span>
              <span style={{ color: 'var(--fg-muted)', marginLeft: 6 }}>
                {halfConfig.length > 0 && (
                  <>
                    <b>{halfConfig.length}</b> 个支付商品通道未齐 · 漏配通道首购不发放
                  </>
                )}
                {halfConfig.length > 0 && unassigned.length > 0 && ' ；'}
                {unassigned.length > 0 && (
                  <>
                    <b>{unassigned.length}</b> 行缺 access_level（未归组，不进 C 端 serving）
                  </>
                )}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* 小时阅读卡产品位齐全性（§2.5，dd614）*/}
      <ReadingPassCoverageCard coverage={readingPassCoverage} platform={app.platform} />

      {/* 统计卡 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12 }}>
        {[
          { label: '支付商品', v: stat.products, sub: `${stat.channelRows} 通道行` },
          { label: '充值', v: stat.recharge },
          { label: '订阅', v: stat.sub },
          { label: '已上架通道', v: stat.active, tone: 'pos' as const },
          {
            label: '不可售通道',
            v: stat.unserveable,
            tone: stat.unserveable > 0 ? ('neg' as const) : undefined,
          },
        ].map((s) => (
          <div key={s.label} className="card" style={{ padding: 14 }}>
            <div className="stat-label">{s.label}</div>
            <div
              className="stat-value"
              style={{
                fontSize: 22,
                marginTop: 4,
                color:
                  s.tone === 'neg' ? 'var(--neg)' : s.tone === 'pos' ? 'var(--pos)' : undefined,
              }}
            >
              {s.v}
            </div>
            {s.sub && (
              <div
                className="mono"
                style={{ fontSize: 10.5, color: 'var(--fg-faint)', marginTop: 2 }}
              >
                {s.sub}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* 筛选行 */}
      <div className="hstack" style={{ gap: 8, flexWrap: 'wrap', rowGap: 8 }}>
        <SearchInput
          width={240}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="搜索名称 / vendor_product_id / access_level"
        />
        <FilterButton
          label="类型"
          value={kinds}
          onChange={setKinds}
          width={120}
          options={[
            { value: 'CONSUMABLE', label: '充值', count: stat.recharge },
            { value: 'SUBSCRIPTION', label: '订阅', count: stat.sub },
          ]}
        />
        <FilterButton
          label="通道"
          value={channels}
          onChange={setChannels}
          width={170}
          options={channelFilterOptions}
        />
        <FilterButton
          label="状态"
          value={statuses}
          onChange={setStatuses}
          width={120}
          options={[
            { value: '1', label: '上架', count: stat.active },
            { value: '0', label: '停用', count: stat.disabled },
          ]}
        />
        <FilterButton
          label="支付页面"
          value={pageIds}
          onChange={setPageIds}
          searchable
          width={230}
          emptyLabel={
            adaptyCatalog.isError ? '该 App 未接入 Adapty / 同步失败' : '该 App 暂无支付页面'
          }
          options={paymentPages.data.map((pg) => {
            const n = pageAccessLevels.get(pg.id)?.size ?? 0;
            // 0 条不是「这页没商品」，多半是链路某环没配 —— 成因写进 label（FilterButton 不渲染 hint），
            // 两种成因分开说：未配落点要去支付页面配 placement，落点无关联要去 Adapty 关联面做关联。
            const why =
              n > 0
                ? ''
                : Object.keys(pg.placements).length === 0
                  ? ' · 未配 placement'
                  : ' · 落点无已关联商品';
            return { value: pg.id, label: `${pg.name}${why}`, count: n };
          })}
        />
        <FilterButton
          label="支付商品"
          value={levels}
          onChange={setLevels}
          searchable
          width={200}
          options={levelOptions}
        />
        {activeFilter && (
          <button
            className="btn btn-ghost btn-sm"
            title="清除全部筛选"
            onClick={() => {
              setQ('');
              setKinds([]);
              setChannels([]);
              setStatuses([]);
              setLevels([]);
              setPageIds([]);
            }}
          >
            <Icons.IconX size={11} /> 清除
          </button>
        )}
        <div style={{ flex: 1 }} />
        <div className="seg">
          <button type="button" onClick={() => collapseAll(false)}>
            全部展开
          </button>
          <button type="button" onClick={() => collapseAll(true)}>
            全部折叠
          </button>
        </div>
        <button
          className="btn btn-ghost btn-sm"
          title="导出全部 product_metadata 行 CSV"
          disabled={empty}
          onClick={exportCsv}
        >
          <Icons.IconDownload size={11} />
        </button>
      </div>

      {/* 分组表 */}
      {isLoading && rows.length === 0 ? (
        <div
          className="card"
          style={{ padding: '48px 0', textAlign: 'center', color: 'var(--fg-muted)' }}
        >
          加载中…
        </div>
      ) : empty ? (
        <div className="card" style={{ padding: '52px 0', textAlign: 'center' }}>
          <Icons.IconCoin size={26} style={{ color: 'var(--fg-faint)' }} />
          <div style={{ fontSize: 13.5, color: 'var(--fg-muted)', marginTop: 10 }}>
            {app.name} 下暂无支付产品
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--fg-faint)', marginTop: 4 }}>
            请先在 Adapty 添加 product，再回本后台手输 vendor_product_id + 录入业务效果
          </div>
          {canWrite && !noApp && (
            <button
              className="btn btn-sm btn-accent"
              style={{ marginTop: 16 }}
              onClick={() => setEditor({})}
            >
              <Icons.IconPlus size={11} /> 新建支付商品
            </button>
          )}
        </div>
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          {/*
            列头（列宽与 ChannelRow 一致：num 74 / 有效期 72 / 状态 62 / act 72）。订阅每计费周期一次性发放的
            bonus 走 BONUS 列；冻结列 daily_grant_amount 不在 A8 §1 响应里，不设列。
          */}
          <div
            className="hstack"
            style={{
              gap: 10,
              padding: '9px 14px',
              background: 'var(--bg-subtle)',
              borderBottom: '1px solid var(--border)',
              fontSize: 10.5,
              fontWeight: 500,
              color: 'var(--fg-muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>支付商品 · 通道</div>
            <div style={{ width: 74, textAlign: 'right' }}>COIN</div>
            <div style={{ width: 74, textAlign: 'right' }}>BONUS</div>
            <div style={{ width: 74, textAlign: 'right' }}>赠送比例</div>
            <div style={{ width: 72 }}>有效期</div>
            <div style={{ width: 62 }}>状态</div>
            <div style={{ width: 72 }} />
          </div>
          {filteredGroups.length === 0 && filteredUnassigned.length === 0 ? (
            <div
              style={{
                padding: '36px 0',
                textAlign: 'center',
                fontSize: 12.5,
                color: 'var(--fg-muted)',
              }}
            >
              没有符合筛选条件的产品
            </div>
          ) : (
            filteredGroups.map(({ g, chans }) => (
              <ProductGroupRow
                key={g.accessLevelId}
                g={g}
                chans={chans}
                platform={app.platform}
                collapsed={!!collapsed[g.accessLevelId]}
                canWrite={canWrite}
                canManage={canManage}
                onToggle={() => toggleCollapse(g.accessLevelId)}
                onEdit={(row) => setEditor({ item: row, pairedChannels: g.channels })}
                onAddChannel={(channel) =>
                  setEditor({
                    prefillTitle: g.name,
                    prefillAccessLevel: g.accessLevelId,
                    prefillEffectFrom: g.channels[0],
                    prefillPaymentMethod: channel,
                    existingChannels: g.channels.map((c) => c.paymentMethod),
                    // 配对行（STRIPE / 框架通道）派生下限同一公式，取组内已有的一条作新建提示
                    prefillBonusFloor:
                      g.channels.find(
                        (c) =>
                          isIapPairedSettlementChannel(c.paymentMethod) &&
                          c.derivedMinimumBonusAmount !== null,
                      )?.derivedMinimumBonusAmount ?? undefined,
                  })
                }
                onEditPrice={(row) => setPricing({ row, productName: g.name })}
                onActivate={(row) => toggleMutation.mutate({ id: row.id, active: true })}
                onDeactivate={(row) => setDeactivating(row)}
                onDelete={(row) => setDeleting(row)}
                onReplace={() => setReplacing(g)}
                onReplaceAndShelfOthers={
                  otherActivePassIds(g.accessLevelId).length > 0
                    ? () => setReplacingOthers(g)
                    : undefined
                }
                onGroupActivate={() =>
                  groupToggleMutation.mutate({
                    rows: g.channels.filter((c) => !c.isActive),
                    active: true,
                  })
                }
                onGroupDeactivate={() => setDeactivatingGroup(g)}
              />
            ))
          )}
        </div>
      )}

      {/* 未归组分组（缺 access_level，只能删除重建） */}
      {showUnassigned && filteredUnassigned.length > 0 && (
        <UnassignedSection
          rows={filteredUnassigned}
          platform={app.platform}
          canManage={canManage}
          onDelete={(row) => setDeleting(row)}
        />
      )}

      {/* Adapty 产品关联面板（③：镜像 product_id ↔ access_level 手动关联，替掉旧启发式镜像卡）*/}
      {!noApp && <PaywallAdaptyLinks app={app} groups={groups} channelRowsReady={isSuccess} />}

      <div className="card-flat" style={{ padding: '8px 12px', background: 'var(--bg-subtle)' }}>
        <div
          className="hstack"
          style={{ gap: 8, fontSize: 11.5, color: 'var(--fg-muted)', alignItems: 'flex-start' }}
        >
          <Icons.IconInfo size={11} style={{ color: 'var(--info)', marginTop: 2, flexShrink: 0 }} />
          <span>
            vendor_product_id / product_type 创建后不可改（改 = 删旧行 + 加新行）· access_level_id
            为空的行不进 C 端 serving · 所有改动记入操作日志。数据契约以 dev-docs #387 §2 为准。
          </span>
        </div>
      </div>

      {editor && (
        // ProductMetadataEditor 成功时内部 invalidate ['product-metadata', appId] + 关闭；onClose 仅清 state
        <ProductMetadataEditor
          appId={app.id}
          item={editor.item}
          prefillTitle={editor.prefillTitle}
          prefillAccessLevel={editor.prefillAccessLevel}
          prefillEffectFrom={editor.prefillEffectFrom}
          pairedChannels={editor.pairedChannels}
          prefillPaymentMethod={editor.prefillPaymentMethod}
          existingChannels={editor.existingChannels}
          prefillBonusFloor={editor.prefillBonusFloor}
          platform={app.platform}
          onClose={() => setEditor(null)}
        />
      )}
      {pricing && (
        <ChannelPriceEditorDialog
          appId={app.id}
          row={pricing.row}
          productName={pricing.productName}
          onClose={() => setPricing(null)}
        />
      )}
      {deleting &&
        (isFrameworkSettlementChannel(deleting.paymentMethod) ? (
          // 框架通道行：软删连带软删其价格行，复活不恢复价格（A8 §2 / §4），确认文案写明后果（设计稿 B4）
          <ConfirmDialog
            title={`删除 ${channelStoreLabel(deleting.paymentMethod, app.platform)} 通道行？`}
            message={
              <>
                将移除该通道的业务效果配置，
                <b style={{ color: 'var(--fg)' }}>它的价格会一并软删</b>
                。之后恢复这一行时<b style={{ color: 'var(--fg)' }}>价格不会恢复</b>
                ，需要重新配置。
              </>
            }
            details={[
              `${deleting.vendorProductId} · ${
                deleting.channelPricesSummary && deleting.channelPricesSummary.length > 0
                  ? deleting.channelPricesSummary.map(formatChannelPriceTag).join(' / ')
                  : '未定价'
              }`,
              'vendor_product_id 创建后不可改，删除后如需恢复须重新添加',
            ]}
            tone="danger"
            confirmLabel="删除"
            requireTyping="DELETE"
            onConfirm={() => deleteMutation.mutate(deleting.id)}
            onClose={() => setDeleting(null)}
          />
        ) : (
          <ConfirmDialog
            title={`删除 ${channelStoreLabel(deleting.paymentMethod, app.platform)} 通道行？`}
            message={
              '将移除该通道的业务效果配置。vendor_product_id 创建后不可改，删除后如需恢复须重新添加。'
            }
            tone="danger"
            confirmLabel="永久删除"
            requireTyping="DELETE"
            onConfirm={() => deleteMutation.mutate(deleting.id)}
            onClose={() => setDeleting(null)}
          />
        ))}
      {deactivating && (
        <ConfirmDialog
          title={`停用 ${channelStoreLabel(deactivating.paymentMethod, app.platform)} 通道？`}
          message="停用后该通道新购不再发放 coin/bonus（C 端 webhook 反查不命中）。业务效果保留。"
          tone="warn"
          confirmLabel="停用"
          requireTyping="STOP"
          onConfirm={() => toggleMutation.mutate({ id: deactivating.id, active: false })}
          onClose={() => setDeactivating(null)}
        />
      )}
      {deactivatingGroup && (
        <ConfirmDialog
          title={`整组下架「${deactivatingGroup.name}」？`}
          message={`将停用该支付商品下全部 ${deactivatingGroup.channels.filter((c) => c.isActive).length} 个在架通道，C 端新购不再发放 coin/bonus。业务效果保留。`}
          tone="warn"
          confirmLabel="整组下架"
          onConfirm={() =>
            groupToggleMutation.mutate({
              rows: deactivatingGroup.channels.filter((c) => c.isActive),
              active: false,
            })
          }
          onClose={() => setDeactivatingGroup(null)}
        />
      )}
      {replacing && (
        <ReplaceTierConfirm
          group={replacing}
          platform={app.platform}
          onConfirm={() =>
            replaceMutation.mutate({ accessLevelId: replacing.accessLevelId, deactivate: [] })
          }
          onEditPrice={(row) => {
            setReplacing(null);
            setPricing({ row, productName: replacing.name });
          }}
          onClose={() => setReplacing(null)}
        />
      )}
      {replacingOthers && (
        <ConfirmDialog
          title="换代并下架其他畅读卡？"
          message={
            <>
              将上架「{replacingOthers.name}」，并下架其余{' '}
              {otherActivePassIds(replacingOthers.accessLevelId).length}{' '}
              个在架畅读卡位。下架后该卡位在途成交会 fail-loud 进 DLQ。推荐只换代上架、不动旧卡。
              <UnpricedTierWarning
                group={replacingOthers}
                platform={app.platform}
                onEditPrice={(row) => {
                  setReplacingOthers(null);
                  setPricing({ row, productName: replacingOthers.name });
                }}
              />
            </>
          }
          tone="warn"
          confirmLabel="换代并下架其他卡"
          onConfirm={() =>
            replaceMutation.mutate({
              accessLevelId: replacingOthers.accessLevelId,
              deactivate: otherActivePassIds(replacingOthers.accessLevelId),
            })
          }
          onClose={() => setReplacingOthers(null)}
        />
      )}
    </div>
  );
}

/**
 * 小时阅读卡卡位通道配对卡（#773 / 70 §2.5，设计稿 B1）：同一 `tier_id` 需要一条 IAP 行 + 任一参与的非 IAP 行
 * （Stripe 或 PayerMax），不要求为本 App 不用的通道建行。框架通道参与行未定价时卡位已配对、但仍不可售。
 */
function ReadingPassCoverageCard({
  coverage,
  platform,
}: {
  coverage: ReadingPassCoverage;
  platform: string | undefined;
}) {
  if (!coverage.configured) return null;
  return (
    <div className="card" style={{ padding: '14px 16px' }}>
      <div className="hstack" style={{ gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 500 }}>小时阅读卡 · 卡位通道配对</span>
        <span
          className={`chip ${coverage.incomplete ? 'chip-warn' : 'chip-pos'}`}
          style={{ fontSize: 10 }}
        >
          {coverage.incomplete ? '有卡位缺参与通道' : '已出现的卡位均已配对'}
        </span>
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--fg-muted)', marginTop: 4 }}>
        同一 <span className="mono">tier_id</span> 需要一条 IAP 行 + 任一参与的非 IAP 行（
        {pairedChannelNames(platform)}）。缺配对时报{' '}
        <span className="mono">READING_PASS_TIER_INCOMPLETE</span>。
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
          gap: 10,
          marginTop: 10,
        }}
      >
        {coverage.tiers.map((tier) => {
          const incomplete = tier.gaps.length > 0;
          return (
            <div
              key={tier.tierId}
              className="card-flat"
              style={{
                padding: '10px 12px',
                ...(incomplete
                  ? { background: 'var(--warn-soft)', borderColor: 'transparent' }
                  : {}),
              }}
            >
              <div className="hstack" style={{ gap: 6 }}>
                <span className="mono" style={{ fontWeight: 600, fontSize: 12 }}>
                  {tier.tierId}
                </span>
                <span
                  className={`chip ${incomplete ? 'chip-warn' : 'chip-pos'}`}
                  style={{ fontSize: 9.5 }}
                >
                  {incomplete ? '缺参与通道' : '已配对'}
                </span>
              </div>
              <div className="hstack" style={{ gap: 6, marginTop: 6, flexWrap: 'wrap', rowGap: 4 }}>
                {tier.presentPaymentMethods.map((m, i) => (
                  <span key={m} className="hstack" style={{ gap: 6 }}>
                    {i > 0 && <span style={{ color: 'var(--fg-faint)' }}>+</span>}
                    <span className={channelChipClass(m)} style={{ fontSize: 9 }}>
                      {channelShortLabel(m, platform)}
                    </span>
                  </span>
                ))}
                {tier.gaps.includes('IAP') && (
                  <span className="chip chip-warn" style={{ fontSize: 9 }}>
                    {channelRowGapLabel('IAP', platform)}
                  </span>
                )}
                {tier.gaps.includes('NON_IAP') && (
                  <span style={{ color: 'var(--fg-faint)', fontSize: 11 }}>
                    + {pairedChannelNames(platform)} 任一
                  </span>
                )}
              </div>
              {tier.unpricedPaymentMethods.length > 0 && (
                <div style={{ fontSize: 11, color: 'var(--warn)', marginTop: 6 }}>
                  {tier.unpricedPaymentMethods
                    .map((m) => channelShortLabel(m, platform))
                    .join(' / ')}{' '}
                  行未定价，该卡位在这些通道上暂不可售
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * 换代确认里的「框架通道参与行未定价」提示（设计稿 B4）：配对已齐，但该通道上仍选不出价格、不可售；
 * App「开启阅读卡」要求每个已启用通道至少一张选得出价格的卡，可能因此被拦下。给「设置价格」捷径。
 * 无未定价参与行时不渲染。
 */
function UnpricedTierWarning({
  group,
  platform,
  onEditPrice,
}: {
  group: LogicalProduct;
  platform: string | undefined;
  onEditPrice: (row: ProductMetadataRow) => void;
}) {
  const unpriced = group.channels.filter((c) =>
    c.validationIssues.includes('CHANNEL_PRICE_UNSELECTABLE'),
  );
  const firstUnpriced = unpriced[0];
  if (!firstUnpriced) return null;
  const names = unpriced.map((c) => channelShortLabel(c.paymentMethod, platform)).join(' / ');
  return (
    <div
      style={{
        marginTop: 10,
        padding: '8px 10px',
        background: 'var(--warn-soft)',
        borderRadius: 'var(--radius)',
        fontSize: 12,
        color: 'var(--fg)',
      }}
    >
      {names} 行还没有价格，换代后该卡位在 {names}{' '}
      上仍不可售；App「开启阅读卡」要求每个已启用通道至少一张选得出价格的卡，可能因此被拦下。可以先
      <button
        type="button"
        className="focusable"
        style={{
          border: 0,
          background: 'none',
          padding: 0,
          font: 'inherit',
          color: 'var(--accent-fg)',
          cursor: 'pointer',
        }}
        onClick={() => onEditPrice(firstUnpriced)}
      >
        设置价格
      </button>
      。
    </div>
  );
}

/** 换代上架确认（A8 §5.1，设计稿 B4）：列出该卡位的参与行，框架通道参与行未定价时给提示。 */
function ReplaceTierConfirm({
  group,
  platform,
  onConfirm,
  onEditPrice,
  onClose,
}: {
  group: LogicalProduct;
  platform: string | undefined;
  onConfirm: () => void;
  onEditPrice: (row: ProductMetadataRow) => void;
  onClose: () => void;
}) {
  return (
    <ConfirmDialog
      title={`换代上架卡位 ${group.effect.tierId ?? group.accessLevelId}`}
      message={
        <>
          参与行：
          {group.channels.map((c) => channelShortLabel(c.paymentMethod, platform)).join(' + ')}。
          <UnpricedTierWarning group={group} platform={platform} onEditPrice={onEditPrice} />
        </>
      }
      confirmLabel="换代上架"
      onConfirm={onConfirm}
      onClose={onClose}
    />
  );
}

// =========================================================
// 分组头 + 通道子行
// =========================================================
function ProductGroupRow({
  g,
  chans,
  platform,
  collapsed,
  canWrite,
  canManage,
  onToggle,
  onEdit,
  onAddChannel,
  onEditPrice,
  onActivate,
  onDeactivate,
  onDelete,
  onGroupActivate,
  onGroupDeactivate,
  onReplace,
  onReplaceAndShelfOthers,
}: {
  g: LogicalProduct;
  chans: ProductMetadataRow[];
  platform: string | undefined;
  collapsed: boolean;
  canWrite: boolean;
  canManage: boolean;
  onToggle: () => void;
  onEdit: (row: ProductMetadataRow) => void;
  /** 补通道：`channel` = 预选的通道（「补通道 · X」）*/
  onAddChannel: (channel: SettlementChannel) => void;
  onEditPrice: (row: ProductMetadataRow) => void;
  onActivate: (row: ProductMetadataRow) => void;
  onDeactivate: (row: ProductMetadataRow) => void;
  onDelete: (row: ProductMetadataRow) => void;
  onGroupActivate: () => void;
  onGroupDeactivate: () => void;
  onReplace: () => void;
  onReplaceAndShelfOthers?: () => void;
}) {
  const rd = g.readiness;
  const activeChannels = g.channels.filter((c) => c.isActive).length;
  const allActive = g.channels.length > 0 && activeChannels === g.channels.length;
  // 畅读卡配对启停必须走原子换代：整组上架是并发 activate，配对行会在 IAP 未提交时失败并留下半发布。
  const canReplace =
    canManage && g.effect.effect === 'READING_PASS_CARD' && rd.gaps.length === 0 && !allActive;
  // 还能补的通道（已有行的不再出现；框架通道对订阅置灰并说明原因）
  const addable: AddableChannel[] = addableChannelsOf(g.channels, g.productType);
  // 缺非 IAP 行时「补通道」按钮的默认通道：第一个可补的三方通道
  const defaultNonIapChannel = addable.find(
    (a) => a.channel !== 'IAP' && a.disabledReason === null,
  )?.channel;
  // 订阅商品不出现的框架通道（设计稿 B1：在摘要行写明，而不是让入口静默消失）
  const subscriptionExcluded = isSub(g)
    ? CHANNEL_DISPLAY_ORDER.filter(
        (ch) => isFrameworkSettlementChannel(ch) && !g.channels.some((c) => c.paymentMethod === ch),
      )
    : [];
  const groupStatusChip =
    g.channels.length === 0
      ? { label: '未配置', cls: '' }
      : activeChannels === 0
        ? { label: '全部停用', cls: 'chip-warn' }
        : activeChannels < g.channels.length
          ? { label: '部分停用', cls: 'chip-warn' }
          : { label: '上架', cls: 'chip-info' };

  return (
    <div style={{ borderBottom: '1px solid var(--border)' }}>
      {/* 分组头 */}
      <div
        className="hstack"
        style={{
          gap: 10,
          alignItems: 'flex-start',
          padding: '11px 14px 11px 6px',
          background: rd.level === 'warn' ? 'var(--warn-soft)' : 'var(--bg-subtle)',
        }}
      >
        <ExpandToggleButton expanded={!collapsed} onToggle={onToggle} />
        <div style={{ flex: 1, minWidth: 0, alignSelf: 'center' }}>
          <div className="hstack" style={{ gap: 8, flexWrap: 'wrap', rowGap: 4 }}>
            <span style={{ fontWeight: 600, fontSize: 13.5, color: 'var(--fg-strong)' }}>
              {g.name}
            </span>
            <CopyChipButton
              prefix="access_level"
              value={g.accessLevelId}
              valueColor="var(--accent-fg)"
              copyAs="access_level_id"
            />
            <span className={`chip ${isSub(g) ? 'chip-accent' : ''}`} style={{ fontSize: 9.5 }}>
              {isSub(g) ? <Icons.IconStar size={9} /> : <Icons.IconCoin size={9} />}
              {typeLabel(g)}
              {isSub(g) && g.subscriptionPeriod ? ` · ${g.subscriptionPeriod}` : ''}
            </span>
            <span
              className={`chip ${rd.level === 'warn' ? 'chip-warn' : 'chip-pos'}`}
              style={{ fontSize: 9.5 }}
              title={
                rd.gaps.length
                  ? '齐备 = 一条 IAP 行 + 至少一条非 IAP 行；缺行时首购反查不命中、漏发 coin/bonus'
                  : rd.activeGaps.length
                    ? '停用后在架行凑不齐（一条 IAP + 至少一条非 IAP），首购反查不命中、漏发 coin/bonus'
                    : rd.disabled.length
                      ? '在架行已齐备；停用的通道不再下发，该通道的在途成交反查不命中、不发放'
                      : '通道齐备'
              }
            >
              {rd.level === 'warn' ? <Icons.IconAlert size={9} /> : <Icons.IconCheck size={9} />}
              {rd.gaps.length
                ? `${rd.activeCount}/${rd.total} · ${rd.gaps.map((gap) => channelRowGapLabel(gap, platform)).join(' / ')}`
                : rd.disabled.length
                  ? `${rd.activeCount}/${rd.total} · ${rd.disabled.map((m) => channelStoreLabel(m, platform)).join(' / ')} 已停用`
                  : `${rd.activeCount}/${rd.total} 通道`}
            </span>
            {/*
              订阅效果（70 §2）：周期发币 = 每计费周期（首购 / 续订 / RESUBSCRIBE）一次性发放 coin + bonus；
              取组内首行（product 级字段，跨通道不一致由 validation_issues 的 PRODUCT_FIELDS_MISMATCH 表达）
            */}
            {isSub(g) && (
              <span
                className="chip chip-info"
                style={{ fontSize: 9.5 }}
                title={
                  g.channels[0]?.subscriptionEffect === 'VIP_READING'
                    ? 'VIP_READING：不发币（coin / bonus 恒 0）；原生 App 尚未开放履约，不进 serving'
                    : 'COIN_PERIOD_GRANT：每计费周期（首购 / 续订 / RESUBSCRIBE）一次性发放该周期 COIN + BONUS'
                }
              >
                {subscriptionEffectLabel(g.channels[0]?.subscriptionEffect ?? null)}
              </span>
            )}
            {/* 权益效果（§2.5）：发币 / 小时阅读卡（阅读卡额外带产品位与时长）。订阅走上面的订阅效果徽章 */}
            {!isSub(g) && (
              <span
                className={`chip ${effectChipClass(g)}`}
                style={{ fontSize: 9.5 }}
                title={
                  g.effect.effect === 'READING_PASS_CARD'
                    ? `产品位 tier_id=${g.effect.tierId ?? '缺失'} · 时长 ${g.effect.durationSeconds ?? '缺失'} 秒（已发布则不可改时长，请走换代）`
                    : g.effect.effect === null
                      ? 'CONSUMABLE 行未登记 consumable_effect：C 端履约无从分派、不进 serving'
                      : '一次性购买成交后按该效果单点分派履约'
                }
              >
                {effectBrief(g)}
              </span>
            )}
            {/* 阅读卡在架 = 时长被 A8 §3 生命周期 gate 锁住；把前置条件挂在这里，运营开弹窗前就看得见 */}
            {g.effect.effect === 'READING_PASS_CARD' && g.effect.durationLocked && (
              <span
                className="chip chip-warn"
                style={{ fontSize: 9.5 }}
                title={`该卡位已发布，不可改时长（已发布通道：${g.effect.durationBlockingChannels
                  .map((m) => channelStoreLabel(m, platform))
                  .join(' / ')}）`}
              >
                时长已锁 · 请走换代
              </span>
            )}
            {g.effect.divergent && (
              <span
                className="chip chip-neg"
                style={{ fontSize: 9.5 }}
                title="各通道的 consumable_effect / consumable_effect_params 不一致 —— 跨通道一致是 is_serveable 前置，配对行（Stripe / PayerMax）会因此不可售（§2.5）"
              >
                <Icons.IconAlert size={9} />
                通道效果不一致
              </span>
            )}
            {g.benefitDivergent.any && (
              <span
                className="chip chip-warn"
                style={{ fontSize: 9.5 }}
                title="各通道权益不一致（coin / bonus / 比例 / 有效期）。配对行（Stripe / PayerMax）按 A8 §1 派生下限的合法上浮不计入 —— 亮起即真配置分歧"
              >
                <Icons.IconAlert size={9} />
                通道权益不一致
              </span>
            )}
            {/* #465 可售 gate：有 active 但不可售通道 → 首购不进 serving，发布前须修基线 / 权益 */}
            {g.serveability.warn && (
              <span
                className="chip chip-neg"
                style={{ fontSize: 9.5 }}
                title={`不可售通道：${g.serveability.unserveable
                  .map((m) => channelStoreLabel(m, platform))
                  .join(' / ')}（首购不进 C 端 serving）`}
              >
                <Icons.IconAlert size={9} />
                {g.serveability.unserveable.length} 通道不可售
              </span>
            )}
          </div>
          {/* 聚合摘要：各通道行的权益简写（框架通道另带价格），缺口另列 */}
          <div className="hstack" style={{ gap: 6, marginTop: 5, flexWrap: 'wrap', rowGap: 4 }}>
            {CHANNEL_DISPLAY_ORDER.map((ch) => {
              const c = g.channels.find((x) => x.paymentMethod === ch);
              if (!c) return null;
              const Ico = channelIcon(ch, platform);
              const price = channelPriceSummaryState(c.channelPricesSummary);
              return (
                <span
                  key={ch}
                  className="hstack"
                  style={{ gap: 5, opacity: c.isActive ? 1 : 0.55 }}
                >
                  <span className={channelChipClass(ch)} style={{ fontSize: 9 }}>
                    <Ico size={10} />
                    {channelStoreLabel(ch, platform)}
                  </span>
                  <span className="mono" style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
                    {benefitBrief(c)}
                    {price.kind === 'priced' &&
                      ` · ${price.prices.map(formatChannelPriceMoney).join(' / ')}`}
                    {price.kind === 'unpriced' && ' · 未定价'}
                  </span>
                  {!c.isActive && (
                    <span style={{ fontSize: 10, color: 'var(--warn)' }}>· 停用</span>
                  )}
                </span>
              );
            })}
            {rd.gaps.map((gap) => (
              <span
                key={gap}
                className="chip chip-warn"
                style={{ fontSize: 9.5, padding: '0 6px' }}
              >
                {channelRowGapLabel(gap, platform)}
              </span>
            ))}
            {subscriptionExcluded.length > 0 && (
              <span style={{ fontSize: 11, color: 'var(--fg-faint)' }}>
                {subscriptionExcluded.map((ch) => channelShortLabel(ch, platform)).join(' / ')}{' '}
                只售一次性商品，订阅商品不能补该通道
              </span>
            )}
          </div>
        </div>
        <div style={{ alignSelf: 'center' }}>
          <span className={`chip ${groupStatusChip.cls}`} style={{ fontSize: 9.5 }}>
            {groupStatusChip.label}
          </span>
        </div>
        <div style={{ alignSelf: 'center' }}>
          <ContextMenu
            align="right"
            items={[
              {
                label: '编辑首个通道',
                icon: 'IconEdit',
                onClick: () => g.channels[0] && onEdit(g.channels[0]),
              },
              // 「补通道 · X」逐通道列出：订阅商品的框架通道置灰并写明原因；非写角色置灰（不静默隐藏）
              ...addable.map((a) => ({
                label: `补通道 · ${channelShortLabel(a.channel, platform)}`,
                icon: 'IconPlus' as const,
                disabled: !canWrite || a.disabledReason !== null,
                title: a.disabledReason ?? (canWrite ? undefined : '需要 operator 及以上权限'),
                onClick: () => onAddChannel(a.channel),
              })),
              ...(g.channels.length > 0
                ? [
                    '---' as const,
                    ...(allActive
                      ? [
                          {
                            label: '整组下架',
                            icon: 'IconPause' as const,
                            disabled: !canManage,
                            onClick: onGroupDeactivate,
                          },
                        ]
                      : canReplace
                        ? []
                        : [
                            {
                              label: '整组上架',
                              icon: 'IconCheck' as const,
                              disabled: !canManage,
                              onClick: onGroupActivate,
                            },
                          ]),
                    ...(canReplace
                      ? [
                          {
                            label: '换代上架',
                            icon: 'IconRefresh' as const,
                            onClick: onReplace,
                          },
                          ...(onReplaceAndShelfOthers
                            ? [
                                {
                                  label: '换代并下架其他卡',
                                  icon: 'IconBan' as const,
                                  onClick: onReplaceAndShelfOthers,
                                },
                              ]
                            : []),
                        ]
                      : []),
                  ]
                : []),
            ]}
          >
            <button
              type="button"
              className="btn btn-ghost btn-icon"
              style={{ width: 26, height: 26 }}
              title="产品级操作"
            >
              <Icons.IconMoreV size={13} />
            </button>
          </ContextMenu>
        </div>
      </div>

      {/* 通道子行（按大类分组）*/}
      {!collapsed && (
        <div style={{ background: 'var(--bg-elev)' }}>
          {CATEGORY_ORDER.map((cat) => {
            const catChannels = CHANNEL_DISPLAY_ORDER.filter((ch) => channelCategory(ch) === cat);
            const nodes: ReactNode[] = catChannels.map((ch) => {
              const c = chans.find((x) => x.paymentMethod === ch);
              if (!c) return null;
              return (
                <ChannelRow
                  key={ch}
                  g={g}
                  c={c}
                  platform={platform}
                  canWrite={canWrite}
                  canManage={canManage}
                  onEdit={() => onEdit(c)}
                  onEditPrice={() => onEditPrice(c)}
                  onActivate={() => onActivate(c)}
                  onDeactivate={() => onDeactivate(c)}
                  onDelete={() => onDelete(c)}
                />
              );
            });
            // 齐备缺口占位：IAP 区报缺 IAP 行；三方区报缺非 IAP 行（不点名必须补哪一家）
            const gap = cat === 'IAP' ? 'IAP' : 'NON_IAP';
            const gapChannel = cat === 'IAP' ? ('IAP' as const) : defaultNonIapChannel;
            if (rd.gaps.includes(gap))
              nodes.push(
                <div
                  key={`gap-${gap}`}
                  className="hstack"
                  style={{
                    gap: 10,
                    padding: '8px 14px 8px 40px',
                    borderTop: '1px solid var(--border)',
                    background: 'var(--warn-soft)',
                  }}
                >
                  <span className="chip chip-warn" style={{ fontSize: 9.5, padding: '0 6px' }}>
                    <Icons.IconAlert size={9} />
                    {channelRowGapLabel(gap, platform)}
                  </span>
                  <span style={{ flex: 1, fontSize: 11.5, color: 'var(--fg-muted)' }}>
                    {gap === 'IAP'
                      ? '该通道未配置 · 客户端首购反查不命中，会漏发 coin/bonus'
                      : `至少需要一条 ${pairedChannelNames(platform)} 行；不要求为本 App 不用的通道建行`}
                  </span>
                  {canWrite && gapChannel && (
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => onAddChannel(gapChannel)}
                    >
                      <Icons.IconPlus size={11} /> 补通道
                    </button>
                  )}
                </div>,
              );
            if (nodes.every((n) => n === null)) return null;
            return (
              <div key={cat}>
                <div
                  className="hstack"
                  style={{
                    gap: 8,
                    alignItems: 'baseline',
                    padding: '7px 14px 4px 40px',
                    borderTop: '1px solid var(--border)',
                  }}
                >
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                      color: 'var(--fg-muted)',
                    }}
                  >
                    {CATEGORY_LABEL[cat].label}
                  </span>
                  <span style={{ fontSize: 10, color: 'var(--fg-faint)' }}>
                    {CATEGORY_LABEL[cat].hint}
                  </span>
                </div>
                {nodes}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ChannelRow({
  g,
  c,
  platform,
  canWrite,
  canManage,
  onEdit,
  onEditPrice,
  onActivate,
  onDeactivate,
  onDelete,
}: {
  g: LogicalProduct;
  c: ProductMetadataRow;
  platform: string | undefined;
  canWrite: boolean;
  canManage: boolean;
  onEdit: () => void;
  /** 框架通道行：打开价格编辑（root/admin 可改，其余只读查看）*/
  onEditPrice: () => void;
  onActivate: () => void;
  onDeactivate: () => void;
  onDelete: () => void;
}) {
  const Ico = channelIcon(c.paymentMethod, platform);
  const coin = Number(c.coinAmount);
  const divergent = g.benefitDivergent;
  // 框架通道行的原生价格（A8 §1 `channel_prices_summary`）；IAP / STRIPE 行不展示价格
  const isFramework = isFrameworkSettlementChannel(c.paymentMethod);
  const price = channelPriceSummaryState(c.channelPricesSummary);
  const priceUnselectable = c.validationIssues.includes('CHANNEL_PRICE_UNSELECTABLE');
  const priceActionLabel = canManage ? '设置价格' : '查看价格';
  const channelName = channelStoreLabel(c.paymentMethod, platform);
  /*
    #352 direct Checkout 路由 gate 的两半（A8 §9.6）：`vendor_product_id ~ ^prod_…$` 与
    `stripe_price_id ~ ^price_…$`，**契约对两者一视同仁**（「两者缺一或格式错误时」同一后果：
    `POST /payment/sessions` 返 `PAYMENT_STRIPE_CHECKOUT_ERROR`，`is_retryable=false`），
    故这里口径统一、不再一条读着良性一条读着 P0。
    与「不可售」刻意分级不同：这是运营配置事故（chip-warn），**不改** `is_serveable`、不在 serving
    侧隐藏商品，也不在写入侧拦截 —— 契约把可发现性交给告警，所以这两个徽章是唯一的暴露面。
    只判 STRIPE 行：IAP 行的 vendor_product_id 是商店 SKU（`com.…`），与本格式无关。
  */
  const isStripeChannel = c.paymentMethod === 'STRIPE';
  const stripePriceRouteMissing = isStripeChannel && !c.stripePriceId;
  const stripeVendorIdMalformed =
    isStripeChannel && !isCanonicalStripeVendorProductId(c.vendorProductId);
  // 两半同时不合规时合并成一条，避免同一行并排两个说同一件事的徽章
  const checkoutRouteBroken = stripePriceRouteMissing && stripeVendorIdMalformed;
  /*
    两个徽章共用的后果尾巴：**只陈述本项自身的后果，不预测这一行会不会被下发。**
    「只要可售且该 App 已接入 Checkout 就会下发」是取六个条件里的两个当充分条件 ——
    `02-API契约/60-支付与订阅.md` §1 的候选式是四项（策略成员 ∩ placement ∩ active Stripe
    Checkout config ∩ `is_serveable`），其上还叠 SRV-PAY-003（`third_party_payment_unlocked_at
    IS NULL` → 只返 IAP、隐藏候选内全部非 IAP 通道）与 SRV-PAY-033（审核标签收窄）两条用户级门禁；
    `03-数据字典/70-产品配置.md` §3「非成员通道恒不可见」更把通道启停的唯一权威判给成员关系。
    徽章回答不了「会不会下发」，也不该回答（同 `data/adapters/app-payment-methods-adapter.ts`：
    宁可不说，也不说错）。
  */
  const CHECKOUT_ROUTE_IMPACT =
    '本项不改变该行的可售判定；后果只落在这一项上：该行发不起 Stripe direct Checkout —— 它若被下发、用户点击支付，POST /payment/sessions 返回不可重试的错误（配置事故，服务端不拦写入、也不隐藏商品）。该行会不会被下发由本项之外的条件决定，本提示不作判断';
  // 跨通道发散字段高亮（设计 cell()）：该字段各通道不一致时套 accent 底，仅有值时套
  const cell = (k: DivergentKey, node: ReactNode) =>
    divergent[k] ? (
      <span
        style={{
          background: 'var(--accent-soft)',
          color: 'var(--accent-fg)',
          borderRadius: 4,
          padding: '1px 5px',
        }}
      >
        {node}
      </span>
    ) : (
      node
    );
  return (
    <div style={{ borderTop: '1px solid var(--border)', padding: '6px 14px 6px 40px' }}>
      <div
        className="hstack"
        style={{
          gap: 10,
          alignItems: 'center',
          opacity: c.isActive ? 1 : 0.62,
        }}
      >
        <div className="hstack" style={{ flex: 1, minWidth: 0, gap: 8, alignItems: 'center' }}>
          <span
            className={channelChipClass(c.paymentMethod)}
            style={{ fontSize: 9.5, flexShrink: 0 }}
          >
            <Ico size={10} />
            {channelName}
          </span>
          <CopyChipButton value={c.vendorProductId} copyAs="vendor_product_id" />
          {/* 框架通道行的价格摘要（设计稿 B1）：点击进价格编辑；未定价用 neg 色写明 C 端不可售 */}
          {isFramework && price.kind === 'priced' && (
            <button
              type="button"
              className="chip mono focusable"
              style={{ fontSize: 10, flexShrink: 0, cursor: 'pointer', color: 'var(--fg)' }}
              title={`channel_prices_summary · 点击${priceActionLabel}`}
              onClick={onEditPrice}
            >
              {price.prices.map(formatChannelPriceTag).join(' / ')}
            </button>
          )}
          {isFramework && price.kind !== 'priced' && (
            <button
              type="button"
              className="chip chip-neg focusable"
              style={{ fontSize: 10, flexShrink: 0, cursor: 'pointer' }}
              title={
                price.kind === 'unpriced'
                  ? `该行在 ${channelName} 未配置价格 · 点击${priceActionLabel}`
                  : '列表未返回价格汇总 · 点击读取价格'
              }
              onClick={onEditPrice}
            >
              {price.kind === 'unpriced' ? '未定价 · C 端不可售' : '价格未知 · 点击读取'}
            </button>
          )}
          {/* 阅读卡行：产品位 + 时长（发币行的权益在 COIN/BONUS 列，已表达，不重复占位）*/}
          {channelEffectBrief(c) && (
            <span
              className="chip chip-info mono"
              style={{ fontSize: 9, flexShrink: 0 }}
              title="小时阅读卡：consumable_effect_params 的 tier_id（产品位身份）+ duration_seconds（权益时长）"
            >
              {channelEffectBrief(c)}
            </span>
          )}
          {/* #465 可售 gate：active 但不可售 → 首购不进 serving（tooltip 列不可售原因）*/}
          {c.isActive && !c.isServeable && (
            <span
              className="chip chip-neg"
              style={{ fontSize: 9, flexShrink: 0 }}
              title={`不可售：${unserveableReason(c)}（首购不进 C 端 serving）`}
            >
              <Icons.IconAlert size={9} />
              不可售
            </span>
          )}
          {/*
          两半都不合规 → 合并成一条「无 Checkout 路由」，两条补救都写进 tooltip。
          「先删后建」的理由取 `product_metadata_app_access_level_method_unique`
          （`packages/server/src/db/schema/shared/product-metadata.ts:191-195`：
          `(app_id, access_level_id, payment_method)`，谓词 `access_level_id IS NOT NULL AND <> ''
          AND deleted_at IS NULL`），**不是** `product_metadata_app_stripe_price_unique`（同文件
          `:201-205`）—— 后者谓词含 `stripe_price_id IS NOT NULL`，而合并态蕴含 price 为 null，
          那条约束根本不参与。access_level 那条对**任何** STRIPE 行都成立（STRIPE 行
          access_level_id 必填），故同时覆盖合并态与只有 vendor 不合规的分支。
        */}
          {checkoutRouteBroken && (
            <span
              className="chip chip-warn"
              style={{ fontSize: 9, flexShrink: 0 }}
              title={`stripe_price_id 缺失 + vendor_product_id 非 canonical prod_…（direct Checkout 路由两半都不成立）：${CHECKOUT_ROUTE_IMPACT}。stripe_price_id 可直接编辑补上；vendor_product_id 是身份字段、创建后不可改，唯一出路是删除该行后按 prod_ 格式重建 —— 删除前先确认该值没有在途履约或已成交订单（webhook 按 vendor_product_id 反查发放规则），删除是高危操作仅 root/admin 可执行，且次序必须先删后建（同 App 同 access_level 的 active STRIPE 行唯一，而 STRIPE 行 access_level_id 必填，先建必撞 RESOURCE_CONFLICT）`}
            >
              <Icons.IconAlert size={9} />无 Checkout 路由
            </span>
          )}
          {/* 只缺 Price：可直接在编辑器补上，是两者中唯一可就地修复的一半 */}
          {stripePriceRouteMissing && !checkoutRouteBroken && (
            <span
              className="chip chip-warn"
              style={{ fontSize: 9, flexShrink: 0 }}
              title={`缺 stripe_price_id：该行无法发起 Stripe direct Checkout。${CHECKOUT_ROUTE_IMPACT}。补法：编辑该行填入 Stripe Dashboard 上的 price_… 即可，不影响 Adapty 商品展示与付款后 webhook/S3 履约`}
            >
              <Icons.IconAlert size={9} />无 Price 路由
            </span>
          )}
          {/* 只有 vendor id 不合规：身份字段改不了，补救是高危的删行重建，前置条件写进 tooltip */}
          {stripeVendorIdMalformed && !checkoutRouteBroken && (
            <span
              className="chip chip-warn"
              style={{ fontSize: 9, flexShrink: 0 }}
              title={`vendor_product_id 非 canonical prod_…（须为 prod_ + 1~249 位字母或数字，后缀不含 - _ . 等符号）：该行无法发起 Stripe direct Checkout。${CHECKOUT_ROUTE_IMPACT}。vendor_product_id 是身份字段、创建后不可改，唯一出路是删除该行后按 prod_ 格式重建 —— 删除前先确认该值没有在途履约或已成交订单（webhook 按 vendor_product_id 反查发放规则，删早了会漏发 coin/bonus），删除是高危操作仅 root/admin 可执行，且次序必须先删后建（同 App 同 access_level 的 active STRIPE 行唯一，而 STRIPE 行 access_level_id 必填，先建必撞 RESOURCE_CONFLICT）`}
            >
              <Icons.IconAlert size={9} />
              vendor id 非 prod_
            </span>
          )}
        </div>
        <div className="mono" style={{ width: 74, textAlign: 'right', fontSize: 12 }}>
          {coin > 0 ? (
            cell('coinAmount', groupDigits(c.coinAmount))
          ) : (
            <span style={{ color: 'var(--fg-faint)' }}>—</span>
          )}
        </div>
        <div className="mono" style={{ width: 74, textAlign: 'right', fontSize: 12 }}>
          {cell('bonusAmount', `+${groupDigits(c.bonusAmount)}`)}
        </div>
        <div className="mono" style={{ width: 74, textAlign: 'right', fontSize: 12 }}>
          {Number(c.bonusRatio) ? (
            cell('bonusRatio', `+${Math.round(Number(c.bonusRatio) * 100)}%`)
          ) : (
            <span style={{ color: 'var(--fg-faint)' }}>—</span>
          )}
        </div>
        <div className="mono" style={{ width: 72, fontSize: 12 }}>
          {cell('bonusExpireDays', expireLabel(c.bonusExpireDays))}
        </div>
        <div style={{ width: 62 }}>
          <span className={`chip ${c.isActive ? 'chip-pos' : ''}`} style={{ fontSize: 9.5 }}>
            {c.isActive ? '上架' : '停用'}
          </span>
        </div>
        <div
          className="hstack"
          style={{ width: 72, justifyContent: 'flex-end', gap: 4, flexShrink: 0 }}
        >
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            title="编辑本通道业务效果"
            disabled={!canWrite}
            onClick={onEdit}
          >
            <Icons.IconEdit size={11} />
          </button>
          <ContextMenu
            items={[
              ...(isFramework
                ? [{ label: priceActionLabel, icon: 'IconTag' as const, onClick: onEditPrice }]
                : []),
              c.isActive
                ? {
                    label: '停用通道',
                    icon: 'IconPause',
                    disabled: !canManage,
                    onClick: onDeactivate,
                  }
                : {
                    label: '上架通道',
                    icon: 'IconPlay',
                    disabled: !canManage,
                    onClick: onActivate,
                  },
              {
                label: '复制 vendor_product_id',
                icon: 'IconCopy',
                onClick: () => copyToClipboard(c.vendorProductId, 'vendor_product_id'),
              },
              '---',
              {
                label: '删除通道行',
                icon: 'IconTrash',
                tone: 'danger',
                disabled: !canManage,
                onClick: onDelete,
              },
            ]}
          >
            <button
              type="button"
              className="btn btn-ghost btn-icon"
              style={{ width: 24, height: 24 }}
            >
              <Icons.IconMoreV size={12} />
            </button>
          </ContextMenu>
        </div>
      </div>
      {/* 不可售原因 CHANNEL_PRICE_UNSELECTABLE：直达价格编辑（设计稿 B1）*/}
      {priceUnselectable && (
        <div
          className="hstack"
          style={{
            gap: 8,
            marginTop: 6,
            padding: '6px 10px',
            background: 'var(--neg-soft)',
            borderRadius: 'var(--radius)',
          }}
        >
          <Icons.IconAlert size={12} style={{ color: 'var(--neg)', flexShrink: 0 }} />
          <span style={{ fontSize: 11.5, flex: 1 }}>
            {channelName} 未配置可用价格：未配价、多个币种行或币种不可定价，该行在 C 端不可售。{' '}
            <span className="mono" style={{ color: 'var(--fg-muted)' }}>
              CHANNEL_PRICE_UNSELECTABLE
            </span>
          </span>
          <button type="button" className="btn btn-sm" onClick={onEditPrice}>
            {priceActionLabel}
          </button>
        </div>
      )}
    </div>
  );
}

function UnassignedSection({
  rows,
  platform,
  canManage,
  onDelete,
}: {
  rows: ProductMetadataRow[];
  platform: string | undefined;
  canManage: boolean;
  onDelete: (row: ProductMetadataRow) => void;
}) {
  const [open, setOpen] = useState(true);
  return (
    <div
      className="card"
      style={{ overflow: 'hidden', borderColor: 'var(--warn)', borderStyle: 'dashed' }}
    >
      <button
        type="button"
        className="hstack"
        style={{
          gap: 10,
          width: '100%',
          padding: '11px 14px',
          background: 'var(--warn-soft)',
          border: 0,
          cursor: 'pointer',
          textAlign: 'left',
          color: 'inherit',
        }}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <Icons.IconChevRight
          size={14}
          style={{
            transform: open ? 'rotate(90deg)' : 'none',
            transition: 'transform 0.15s',
            color: 'var(--warn)',
            flexShrink: 0,
          }}
        />
        <Icons.IconAlert size={14} style={{ color: 'var(--warn)', flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 13 }}>
            缺 access_level · {rows.length} 行未归组，不进 C 端 serving
          </div>
          <div style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 2 }}>
            已手输 vendor_product_id，但未填 access level → 无法进入客户端 serving，发布前必须清零。
            access_level_id 创建后不可补填（A8 §3）：先建立该产品的 access level 关联，再由
            root/admin 删除该行并重新录入（同 vendor_product_id 重录走复活，同样仅 root/admin）
          </div>
        </div>
      </button>
      {open && (
        <div style={{ background: 'var(--bg-elev)' }}>
          {rows.map((u) => (
            <div
              key={u.id}
              className="hstack"
              style={{
                gap: 10,
                alignItems: 'center',
                padding: '6px 14px 6px 24px',
                borderTop: '1px solid var(--border)',
              }}
            >
              <div className="hstack" style={{ flex: 1, minWidth: 0, gap: 8 }}>
                <span
                  className={channelChipClass(u.paymentMethod)}
                  style={{ fontSize: 9.5, flexShrink: 0 }}
                >
                  {channelStoreLabel(u.paymentMethod, platform)}
                </span>
                <CopyChipButton value={u.vendorProductId} copyAs="vendor_product_id" />
              </div>
              {/* coin / bonus / 赠送比例（同 ChannelRow 列宽），对齐分组表数值列 */}
              <div className="mono" style={{ width: 74, textAlign: 'right', fontSize: 12 }}>
                {Number(u.coinAmount) > 0 ? (
                  groupDigits(u.coinAmount)
                ) : (
                  <span style={{ color: 'var(--fg-faint)' }}>—</span>
                )}
              </div>
              <div className="mono" style={{ width: 74, textAlign: 'right', fontSize: 12 }}>
                +{groupDigits(u.bonusAmount)}
              </div>
              <div className="mono" style={{ width: 74, textAlign: 'right', fontSize: 12 }}>
                {Number(u.bonusRatio) ? (
                  `+${Math.round(Number(u.bonusRatio) * 100)}%`
                ) : (
                  <span style={{ color: 'var(--fg-faint)' }}>—</span>
                )}
              </div>
              <span className="chip chip-warn" style={{ fontSize: 9.5 }}>
                <Icons.IconAlert size={9} />缺 access_level
              </span>
              <div className="hstack" style={{ gap: 4, flexShrink: 0 }}>
                <ContextMenu
                  items={[
                    {
                      label: '复制 vendor_product_id',
                      icon: 'IconCopy',
                      onClick: () => copyToClipboard(u.vendorProductId, 'vendor_product_id'),
                    },
                    '---',
                    {
                      label: '删除该行',
                      icon: 'IconTrash',
                      tone: 'danger',
                      disabled: !canManage,
                      onClick: () => onDelete(u),
                    },
                  ]}
                >
                  <button
                    type="button"
                    className="btn btn-ghost btn-icon"
                    style={{ width: 24, height: 24 }}
                  >
                    <Icons.IconMoreV size={12} />
                  </button>
                </ContextMenu>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
